<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="filter-group-main">
    <BoxContainer title="Filters Group" style="border-color: lightgray">
      <div class="filter-list">
        <div class="filter-item" v-for="(filterItem, index) in localValue" :key="index">
          <div class="filter-label">
            <span v-if="index > 0" class="filter-join">{{ filterItem.joinType == 'AND' ? 'AND' : 'OR' }}</span>
            {{ findLabel(filterItem.key) }}
          </div>
          <div class="filter-value" v-if="isMultiselectFilter(filterItem)">
            <span
              class="filter-value-chip"
              v-for="(selectedItem, selectedIndex) in getMultiselectDisplayValues(filterItem)"
              :key="selectedIndex"
            >
              {{ selectedItem }}
            </span>
          </div>
          <div class="filter-value" v-else>{{ getFilterValueDisplay(filterItem) }}</div>
        </div>
      </div>
      <Button class="mt-2" @click="openFilters">Edit Filters</Button>
    </BoxContainer>

    <div class="filter-popup" v-if="filterOpen" ref="filterPopup">
      <FiltersPopup
        :value.sync="localValue"
        :filterDefinition="filterDefinition"
        :optionCache.sync="loadedOptions"
        @cancel="onCancel"
        @save="onSave"
      />
    </div>
  </div>
</template>

<script>
//default demo types
const FILTER_TYPES = [
  { label: 'String value', filterType: 'string_value', key: 'string_value' },
  { label: 'Number value', filterType: 'number_value', key: 'number_value' },
  { label: 'String range', filterType: 'string_range', key: 'string_range' },
  { label: 'Date', filterType: 'date', key: 'date' },
  { label: 'Date value', filterType: 'date_value', key: 'date_value' },
  { label: 'Date range', filterType: 'date_range', key: 'date_range' },
  { label: 'Dropdown list', filterType: 'dropdown_list', key: 'dropdown_list' },
  { label: 'Mutliselect', filterType: 'multiselect', key: 'multiselect' },
  { label: 'Boolean', filterType: 'boolean', key: 'boolean' },
  { label: 'Null check', filterType: 'null_check', key: 'null_check' },
];

import FiltersPopup from './FiltersPopup.vue';
export default {
  components: { FiltersPopup },
  computed: {},
  data: function () {
    return { filterOpen: false, localValue: [], loadedOptions: {} };
  },
  props: {
    filterDefinition: {
      type: Array,
      required: true,
      default: () => [],
    },
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  methods: {
    onCancel() {
      this.filterOpen = false;
    },
    onSave() {
      // emit update to parent and close
      this.$emit('update:value', this.localValue);
      this.filterOpen = false;
    },

    async openFilters() {
      await this.loadFilterOptions();
      this.filterOpen = true;
      this.$nextTick(() => {
        this.teleportFilterPopup();
      });
    },

    teleportFilterPopup() {
      const popup = this.$refs.filterPopup;
      const overlayRoot = this.$getOverlayRoot();
      if (!popup || popup.parentNode == overlayRoot) return;
      overlayRoot.appendChild(popup);
    },

    removeFilterPopup() {
      const popup = this.$refs.filterPopup;
      if (popup && popup.parentNode == this.$getOverlayRoot()) {
        popup.parentNode.removeChild(popup);
      }
    },

    getFilterValueDisplay(filterItem) {
      const getFilterType = (this.filterDefinition.length > 0 ? this.filterDefinition : FILTER_TYPES).find(
        (t) => t.key === filterItem.key,
      );
      switch (getFilterType?.filterType) {
        case 'string_value':
          return filterItem.value || '—';
        case 'number_value':
          return this.hasFilterValue(filterItem.value) ? filterItem.value : '—';
        case 'dropdown_list':
          return this.hasFilterValue(filterItem.value) ? this.getSavedOptionLabel(filterItem, filterItem.value) : '—';
        case 'multiselect':
          return filterItem.selected?.length > 0 ?
              filterItem.selected.map((item) => this.getSavedOptionLabel(filterItem, item)).join(', ')
            : '—';
        case 'date':
          return filterItem.value ? new Date(filterItem.value).formatString('yyyy-MM-dd') : '—';
        case 'date_value':
          return new Date(filterItem.value).formatString('yyyy-MM-dd HH:mm') || '—';

        case 'string_range':
          return `${filterItem.fromStr || '—'} to ${filterItem.toStr || '—'}`;
        case 'date_range': {
          if (!filterItem.from) return '—';
          const dateFormat = getFilterType.showTime === false ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm';
          const fromStr = new Date(filterItem.from).formatString(dateFormat);
          const toStr = filterItem.to ? new Date(filterItem.to).formatString(dateFormat) : 'Today';
          return `${fromStr} to ${toStr}`;
        }

        case 'boolean':
          return (
            filterItem.value === true ? 'Yes'
            : filterItem.value === false ? 'No'
            : '—'
          );

        case 'null_check':
          return filterItem.value ? filterItem.value.label : '—';

        default:
          return '—';
      }
    },

    findLabel(key) {
      const def = this.filterDefinition.find((d) => d.key === key);
      return def ? def.label : key;
    },

    getFilterDefinition(filterItem) {
      return this.filterDefinition.find((definition) => definition.key === filterItem.key);
    },

    getFilterOptions(filterItem) {
      const definition = this.getFilterDefinition(filterItem);
      if (definition?.optionsFunction) return this.loadedOptions[definition.key] ?? [];
      return definition?.options ?? [];
    },

    getFilterSaveKey(filterItem) {
      return this.getFilterDefinition(filterItem)?.saveKey || 'key';
    },

    getSavedOptionLabel(filterItem, value) {
      const saveKey = this.getFilterSaveKey(filterItem);
      if (value && typeof value == 'object') return value.label ?? value[saveKey] ?? '';
      const option = this.getFilterOptions(filterItem).find((item) => this.optionValueMatches(item[saveKey], value));
      return option?.label ?? value;
    },

    hasFilterValue(value) {
      return value !== null && value !== undefined && value !== '';
    },

    optionValueMatches(optionValue, savedValue) {
      if (!this.hasFilterValue(optionValue) || !this.hasFilterValue(savedValue)) return optionValue === savedValue;
      return String(optionValue) === String(savedValue);
    },

    isMultiselectFilter(filterItem) {
      return this.getFilterDefinition(filterItem)?.filterType == 'multiselect';
    },

    getMultiselectDisplayValues(filterItem) {
      return filterItem.selected?.length > 0 ?
          filterItem.selected.map((item) => this.getSavedOptionLabel(filterItem, item))
        : ['-'];
    },

    loadFilterOptions() {
      return Promise.all(
        this.localValue.map(async (filterItem) => {
          const definition = this.getFilterDefinition(filterItem);
          if (definition?.optionsFunction && !this.loadedOptions[definition.key]) {
            const result = await definition.optionsFunction();
            this.$set(this.loadedOptions, definition.key, result);
          }
        }),
      );
    },
  },
  mounted() {
    this.localValue = this.value ? [...this.value] : [];
    this.loadFilterOptions();
  },
  beforeDestroy() {
    this.removeFilterPopup();
  },
  watch: {
    value(val) {
      this.localValue = val ? [...val] : [];
      this.loadFilterOptions();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.filter-list {
  display: grid;
  gap: 0.25rem;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
}

.filter-item {
  background-color: #f0f3ff;
  border-radius: 0.25rem;
  border: 1px solid lightgray;
  overflow: hidden;

  padding: 0.25rem 0.5rem;
  padding-bottom: 0.5rem;
  display: flex;
  flex-direction: column;

  .filter-label {
    font-size: 0.65rem;
    color: darkslategrey;
  }

  .filter-join {
    font-size: 0.6rem;
    color: white;
    background: #7c8fd0;
    border-radius: 0.2rem;
    padding: 0.1rem 0.25rem;
  }

  .filter-value {
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem;
    text-wrap: balance;
    margin-top: 0.1rem;
  }

  .filter-value-chip {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    max-width: 100%;
    padding: 0.15rem 0.35rem;
    border-radius: 0.2rem;
    outline: 1px solid #c8d2f0;
    background: #ffffff;
    color: #1f2d4d;
    font-size: 0.75rem;
    line-height: 1.2;
  }
}

.filter-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99990;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.75rem;
}
</style>
